import { Column } from '../DataTable/DataTable';
import { formatDate as formatUserDate, formatLocaleDateTime } from '../../config/appConfig';

interface ExportColumn {
  headerName: string;
  field: string;
  displayField?: string;
  cellRenderer?: string;
  dateFormat?: string;
}

interface ExportToExcelOptions {
  sortedData: any[];
  tableColumns: Column<any>[];
  entityName: string;
  exportFileName: string;
  formatDate?: (dateString?: string) => string;
}

const exportToExcel = async ({
  sortedData,
  tableColumns,
  entityName,
  exportFileName,
  formatDate: formatDateProp = (dateString?: string) => {
    if (!dateString) return '';
    return formatUserDate(dateString);
  },
}: ExportToExcelOptions): Promise<{ success: boolean; fallback?: string }> => {
  try {
    // Dynamic import for ExcelJS (will need to be installed)
    const ExcelJS = await import('exceljs');
    const { saveAs } = await import('file-saver');

    const workbook = new ExcelJS.default.Workbook();
    const worksheet = workbook.addWorksheet('Sheet1');

    // Helper function to format dates - matches table formatting exactly
    const formatDateByPattern = (date: any, pattern?: string): string => {
      if (!date) return '';
      try {
        const d = date instanceof Date ? date : new Date(date);
        if (Number.isNaN(d.getTime())) return '';
        
        // Use the provided formatDate function or default to toLocaleDateString
        if (formatDateProp) {
          return formatDateProp(date instanceof Date ? date.toISOString() : date);
        }

        return formatUserDate(d);
      } catch {
        return '';
      }
    };

    // Helper function to format array/multiselect values
    const formatArrayValue = (value: any, column: ExportColumn, row: any): string => {
      if (!value && !(column.displayField && row && row[column.displayField])) return '';

      // Check if displayField exists and use that value
      let arrayValue = value;
      if (column.displayField && row && row[column.displayField]) {
        arrayValue = row[column.displayField];
      }

      // If not an array, return as is (unless it's a string that might be JSON)
      if (!Array.isArray(arrayValue)) {
        // Try to parse as JSON if it's a string
        if (typeof arrayValue === 'string' && arrayValue.trim().startsWith('[')) {
          try {
            arrayValue = JSON.parse(arrayValue);
          } catch {
            return value;
          }
        } else {
          return value;
        }
      }

      if (arrayValue.length === 0) return '';

      // If array contains objects, extract names
      if (arrayValue.length > 0 && typeof arrayValue[0] === 'object') {
        const names = arrayValue
          .map((item: any) => {
            // Check for common display name fields (same as table renderer)
            return (
              item.userDisplayName ||
              item.name ||
              item.title ||
              item.label ||
              (item.userFirstName && item.userLastName
                ? `${item.userFirstName} ${item.userLastName}`
                : null) ||
              ''
            );
          })
          .filter((name: string) => name); // Remove empty values

        return names.join(', ');
      }

      // If array contains primitive values, join them
      return arrayValue.join(', ');
    };

    // Prepare columns with date formatting info
    // Include ALL columns from tableColumns except 'actions' column
    // This ensures all data columns are exported, regardless of visibility settings
    const columnsToExtract: ExportColumn[] = tableColumns
      .filter((col) => col && col.key && col.key !== 'actions') // Only exclude actions column
      .map((col) => ({
        headerName: col.header || String(col.key) || 'Unknown',
        field: String(col.key),
        displayField: (col as any).displayField,
        cellRenderer: (col as any).cellRenderer,
        dateFormat: (col as any).dateFormat,
      }));

    const headers = columnsToExtract.map((col) => col.headerName);

    // Add report name - ALWAYS derive from exportFileName for consistency
    // This ensures we get the correct entity name regardless of what entityName parameter contains
    const fileNameLower = (exportFileName || '').toLowerCase();
    let displayEntityName = 'Data Export';

    // Map exportFileName patterns to entity names
    if (fileNameLower.includes('complaint')) {
      displayEntityName = 'Complaint';
    } else if (fileNameLower.includes('risk_assessment') || fileNameLower.includes('riskassessment')) {
      displayEntityName = 'Risk Assessment';
    } else if (fileNameLower.includes('capa')) {
      displayEntityName = 'CAPA';
    } else if (fileNameLower.includes('document')) {
      displayEntityName = 'Document';
    } else if (fileNameLower.includes('non_conformity') || fileNameLower.includes('nonconformity')) {
      displayEntityName = 'Non-Conformity Report';
    } else if (fileNameLower.includes('deviation')) {
      displayEntityName = 'Deviation';
    } else if (fileNameLower.includes('client')) {
      displayEntityName = 'Client';
    } else if (exportFileName) {
      // Fallback: try to capitalize and format the exportFileName
      displayEntityName = exportFileName
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }

    // Add rows in the correct order from the start - no insertRow needed
    // Row 1: Title (merged across 5 columns)
    const titleRowData = new Array(columnsToExtract.length).fill('');
    titleRowData[0] = `${displayEntityName} List`;
    worksheet.addRow(titleRowData);
    // Merge first 5 columns for the title
    worksheet.mergeCells(1, 1, 1, Math.min(5, columnsToExtract.length));
    const titleCell = worksheet.getCell(1, 1);
    titleCell.value = `${displayEntityName} List`;
    titleCell.font = { size: 16, bold: true };
    titleCell.alignment = { horizontal: 'left', vertical: 'middle' };

    // Row 2: Date
    const dateTime = formatLocaleDateTime(new Date());
    const dateRowData = new Array(columnsToExtract.length).fill('');
    dateRowData[columnsToExtract.length - 1] = dateTime;
    const dateRow = worksheet.addRow(dateRowData);
    dateRow.getCell(columnsToExtract.length).alignment = { horizontal: 'right' };
    dateRow.getCell(columnsToExtract.length).font = { size: 10 };

    // Row 3: Empty row
    worksheet.addRow(new Array(columnsToExtract.length).fill(''));

    // Row 4: Headers
    const headerRow = worksheet.addRow(headers);

    // Header formatting
    headerRow.eachCell((cell: any) => {
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 12 };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF4A5568' },
      };
      cell.border = {
        top: { style: 'thin', color: { argb: 'FF000000' } },
        left: { style: 'thin', color: { argb: 'FF000000' } },
        bottom: { style: 'thin', color: { argb: 'FF000000' } },
        right: { style: 'thin', color: { argb: 'FF000000' } },
      };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
    });

    // Add data rows - ensure all columns are included with proper value extraction
    sortedData.forEach((row, index) => {
      const newRow = worksheet.addRow(
        columnsToExtract.map((col) => {
          // Try to get value from the field key first
          let value = row[col.field];

          // If value is undefined/null and displayField exists, try displayField
          if ((value === undefined || value === null) && col.displayField && row[col.displayField] !== undefined) {
            value = row[col.displayField];
          }

          // Default to empty string if still no value (ensures all columns are included)
          if (value === undefined || value === null) {
            value = '';
          }

          // Format dates if this is a date column
          // Check if the column has a render function that formats dates
          const column = tableColumns.find((c) => String(c.key) === col.field);
          if (column && column.render) {
            // Try to render the value using the column's render function
            // But we can't use React components, so we'll check the value type instead
            const valueStr = String(value);
            if (valueStr && (valueStr.includes('T') || valueStr.match(/^\d{4}-\d{2}-\d{2}/))) {
              // Looks like a date string, format it
              value = formatDateByPattern(value, col.dateFormat);
            }
          } else if (col.cellRenderer === 'date' && value) {
            value = formatDateByPattern(value, col.dateFormat);
          }
          // Format arrays/multiselect fields (extract names from objects)
          else if (col.cellRenderer === 'array' || Array.isArray(value)) {
            value = formatArrayValue(value, col, row);
          }
          // Handle displayField for arrays
          else if (col.displayField && row[col.displayField] && Array.isArray(row[col.displayField])) {
            value = formatArrayValue(row[col.displayField], col, row);
          }
          // Handle string values
          else if (typeof value === 'string') {
            value = value.replace(/\n/g, '\r\n');
          }
          // Handle objects that might be JSON (fallback)
          else if (typeof value === 'object' && value !== null) {
            // If it's an object but not an array, try to extract name
            if (value.name || value.title || value.label) {
              value = value.name || value.title || value.label;
            } else {
              // Last resort: stringify if it's a complex object
              value = JSON.stringify(value);
            }
          }

          return value;
        })
      );

      // Data row formatting
      newRow.eachCell((cell: any) => {
        cell.border = {
          top: { style: 'thin', color: { argb: 'FFCCCCCC' } },
          left: { style: 'thin', color: { argb: 'FFCCCCCC' } },
          bottom: { style: 'thin', color: { argb: 'FFCCCCCC' } },
          right: { style: 'thin', color: { argb: 'FFCCCCCC' } },
        };
        cell.alignment = {
          vertical: 'top',
          horizontal: 'left',
          wrapText: true,
        };
        // Alternating row colors
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: index % 2 === 0 ? 'FFFFFFFF' : 'FFF9F9F9' },
        };
      });
    });

    // Set column widths
    worksheet.columns = columnsToExtract.map((col) => ({
      width: Math.max(col.headerName.length + 5, 15),
    }));

    // Auto-adjust row heights
    worksheet.eachRow((row: any) => {
      let maxLineCount = 1;
      row.eachCell((cell: any) => {
        const lineCount = cell.value?.toString().split('\r\n').length || 1;
        if (lineCount > maxLineCount) maxLineCount = lineCount;
      });
      row.height = 20 * maxLineCount;
    });

    // Generate and save file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    // Handle both default and named exports for file-saver
    const saveAsFunction = (saveAs as any).default || saveAs;
    saveAsFunction(blob, `${exportFileName}.xlsx`);

    return { success: true };
  } catch (error) {
    console.error('Excel export error:', error);
    // Fallback to enhanced CSV export that opens properly in Excel
    try {
      exportToEnhancedCSV(sortedData, tableColumns, entityName, exportFileName, formatDateProp);
      return { success: true, fallback: 'csv' };
    } catch (fallbackError) {
      console.error('CSV fallback also failed:', fallbackError);
      throw new Error('Failed to export to Excel. Please check the console for details.');
    }
  }
};

const exportToEnhancedCSV = (
  sortedData: any[],
  tableColumns: Column<any>[],
  entityName: string,
  exportFileName: string,
  formatDate?: (dateString?: string) => string
): void => {
  // Enhanced CSV export for when ExcelJS is not available
  // This creates an Excel-compatible file that opens properly in Excel
  const headers = tableColumns.filter((col) => col.key !== 'actions').map((col) => col.header || String(col.key));

  // Add title and timestamp rows for Excel
  const titleRow = `${entityName} Export`;
  const timestampRow = `Generated: ${formatLocaleDateTime(new Date())}`;
  const emptyRow = '';

  const csvRows = [
    titleRow,
    timestampRow,
    emptyRow,
    headers.join('\t'), // Use tabs for better Excel compatibility
    ...sortedData.map((item) =>
      tableColumns
        .filter((col) => col.key !== 'actions')
        .map((col) => {
          let value = item[String(col.key)];
          if (typeof value === 'object' && value !== null) {
            value = JSON.stringify(value);
          }
          // Escape quotes and wrap in quotes if contains comma, tab, or newline
          if (value && (value.includes(',') || value.includes('\t') || value.includes('\n') || value.includes('"'))) {
            value = `"${String(value).replace(/"/g, '""')}"`;
          }
          return value || '';
        })
        .join('\t')
    ),
  ];

  const csvContent = csvRows.join('\n');
  // Use UTF-8 BOM for proper Excel encoding
  const BOM = '\uFEFF';
  const blob = new Blob([BOM + csvContent], {
    type: 'application/vnd.ms-excel;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${exportFileName}.xls`; // Use .xls extension for better Excel compatibility
  a.click();
  URL.revokeObjectURL(url);
};

export { exportToExcel };

